import { ReactNode } from "react";
import { useT } from "@transifex/react";
import { CheckCircleIcon, CheckIcon, InfoIcon, type Icon, type IconWeight } from "@phosphor-icons/react";
import { AreaButton, ButtonVariant } from "../teacherArea/AreaButton";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";
import { PhosphorIcon } from "../../basic/phosphorIcon/PhosphorIcon";
import { PILL_BUTTON_CLASS } from "./styleConstants";

interface ShareStatusBannerProps {
    gradedCount: number;
    totalCount: number;
    allGraded: boolean;
    allShared: boolean;
    canShareInClassroom: boolean;
    hasShareableTargets: boolean;
    isSharing: boolean;
    onGradeClick: () => void;
    onShareAll: () => void;
}

interface BannerShellProps {
    borderColor: string;
    icon: Icon;
    iconColor: string;
    iconWeight?: IconWeight;
    textColor: string;
    message: string;
    trailing: ReactNode;
    layoutClass: string;
}

const BannerShell = (props: BannerShellProps) => (
    <div className={`bg-white border ${props.borderColor} rounded-xsmall-container ${props.layoutClass}`}>
        <div className={`flex items-center gap-s text-l ${props.textColor}`}>
            <PhosphorIcon
                phosphorIcon={props.icon}
                fontSize="text-xl"
                color={props.iconColor}
                weight={props.iconWeight}
            />
            <span>{props.message}</span>
        </div>
        {props.trailing}
    </div>
);

const ShareStatusBanner = (props: ShareStatusBannerProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const t_evaluateAll = t("Evaluate", { _tags: "ShareTestReviews, ShareStatusBanner" });
    const t_shareAll = t("Share all", { _tags: "ShareTestReviews, ShareStatusBanner" });
    const t_sharedBadge = t("Shared in classroom", { _tags: "ShareTestReviews, ShareStatusBanner" });
    const t_progress = t("{gradedCount}/{totalCount} submissions fully evaluated", {
        _tags: "ShareTestReviews, ShareStatusBanner",
        gradedCount: props.gradedCount,
        totalCount: props.totalCount,
    });
    const t_allDone = t("You have evaluated all submissions.", {
        _tags: "ShareTestReviews, ShareStatusBanner",
    });

    const layoutClass = `flex w-full items-center justify-between gap-s ${getStyleBasedOnBreakpoint(
        Breakpoint.MD,
        "flex-row px-[25px] py-[17px]",
        "flex-col items-stretch px-s py-s"
    )}`;

    if (!props.allGraded) {
        return (
            <BannerShell
                borderColor="border-p6-grey-light"
                icon={InfoIcon}
                iconColor="text-p6-grey-dark"
                textColor="text-p6-grey-dark"
                message={t_progress}
                layoutClass={layoutClass}
                trailing={
                    <AreaButton
                        variant={ButtonVariant.PRIMARY}
                        role="button"
                        onClick={props.onGradeClick}
                        className={PILL_BUTTON_CLASS}
                    >
                        {t_evaluateAll}
                    </AreaButton>
                }
            />
        );
    }

    // All submissions are graded. Legacy tests shared without an explicit mode cannot be shared
    // into the classroom, so no action is offered; otherwise show the shared badge or the share-all button.
    const renderGreenTrailing = () => {
        if (!props.canShareInClassroom) {
            return null;
        }
        if (props.allShared) {
            return (
                <div className="flex items-center gap-xs text-l text-p6-green">
                    <PhosphorIcon
                        phosphorIcon={CheckIcon}
                        fontSize="text-xl"
                        color="text-p6-green"
                    />
                    <span>{t_sharedBadge}</span>
                </div>
            );
        }
        // Nothing left to share (e.g. a student is fully evaluated but their share record failed to
        // persist) — don't offer a "Share all" button that would be a no-op.
        if (!props.hasShareableTargets) {
            return null;
        }
        return (
            <AreaButton
                variant={ButtonVariant.PRIMARY}
                role="button"
                onClick={props.onShareAll}
                disabled={props.isSharing}
                className={PILL_BUTTON_CLASS}
            >
                {t_shareAll}
            </AreaButton>
        );
    };

    return (
        <BannerShell
            borderColor="border-p6-green"
            icon={CheckCircleIcon}
            iconColor="text-p6-green"
            iconWeight="fill"
            textColor="text-p6-green"
            message={t_allDone}
            layoutClass={layoutClass}
            trailing={renderGreenTrailing()}
        />
    );
};

export default ShareStatusBanner;
